import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from "@angular/router";

import { EmocionesDiarioService } from "../services/emociones.service";

const STORAGE_KEY = "diarioEmocionesCheck";

/**
 * Redirige al diario de emociones una sola vez al día (al loguearse o al
 * abrir la app en un día nuevo) si el paciente aún no registró su emoción.
 * Tras ese primer aviso el paciente puede navegar libremente.
 */
@Injectable({ providedIn: "root" })
export class DiarioEmocionesGuard {
  constructor(
    private router: Router,
    private emocionesService: EmocionesDiarioService
  ) {}

  async canActivateChild(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean | UrlTree> {
    const currentPatientString = localStorage.getItem("currentPatient");
    if (!currentPatientString) {
      return true; // AuthGuard se encarga de la sesión
    }

    let userId: string;
    try {
      userId = JSON.parse(currentPatientString)?.userId;
    } catch {
      return true;
    }
    if (!userId) {
      return true;
    }

    const hoy = this.formatDate(new Date());
    const checkValue = `${hoy}|${userId}`;

    // Ya se hizo el chequeo hoy: el paciente navega libremente
    if (localStorage.getItem(STORAGE_KEY) === checkValue) {
      return true;
    }

    // Si ya va camino al diario, solo marcamos el chequeo como hecho
    if (state.url.startsWith("/diario-emociones")) {
      localStorage.setItem(STORAGE_KEY, checkValue);
      return true;
    }

    try {
      const registros = await this.emocionesService.getByRangoFechas(
        userId,
        hoy,
        hoy
      );
      localStorage.setItem(STORAGE_KEY, checkValue);
      if (registros.length > 0) {
        return true;
      }
      return this.router.createUrlTree(["/diario-emociones"]);
    } catch (error) {
      // Ante un error de red no bloqueamos la navegación
      console.error("Error consultando el diario de emociones:", error);
      return true;
    }
  }

  private formatDate(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
}
